import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { XMarkIcon, PlusIcon, TrashIcon, PhotoIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { supabase } from '../../utils/supabase';
import { createProduct } from '../../utils/api';

// Helper pour générer des IDs uniques simples
let optionIdCounter = 0;
let valueIdCounter = 0;

// Fonction utilitaire pour générer les combinaisons (produit cartésien)
const cartesian = (...args) => {
  const r = [], max = args.length - 1;
  function helper(arr, i) {
    for (let j = 0, l = args[i].length; j < l; j++) {
      const a = arr.slice(0); // Clone arr
      a.push(args[i][j]);
      if (i === max)
        r.push(a);
      else
        helper(a, i + 1);
    }
  }
  if (args.length > 0) {
     helper([], 0);
  }
  return r;
};

const ProductForm = ({ product, onClose, onSave }) => {
  const isEditMode = !!product;
  
  const [productOptions, setProductOptions] = useState([]); // ex: [{ id: o1, name: 'Couleur', values: [{id: v1, name: 'Rouge'}, {id: v2, name: 'Bleu'}] }]
  
  const [isSubmitting, setIsSubmitting] = useState(false); // Nouvel état pour le chargement de la soumission
  const [submitError, setSubmitError] = useState(null); // Erreur lors de la soumission
  
  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    category: '',
    subCategory: '',
    price: '', // Prix de base si pas de variations
    discountPrice: '',
    status: 'active',
    stockLevel: '', // Stock de base si pas de variations
    description: '',
    // Structure d'images multiples
    images: [], // Array d'objets: { id, file?, previewUrl?, existingUrl?, alt_text, position, is_main, option_values? }
    // option_values: { "Couleur": "Rouge" } pour associer à une valeur d'option spécifique
    barcode: '',
    has_variants: false,
    options: [], // Sera dérivé de productOptions lors de la sauvegarde
    variations: [] // Chaque variation aura ses images spécifiques dans le tableau images
  });

  const [errors, setErrors] = useState({});
  
  // État pour le modal de sélection d'images pour variants
  const [imageSelectionModal, setImageSelectionModal] = useState({
    isOpen: false,
    variant: null, // Objet variant complet
    mode: 'variant' // 'variant' ou 'option_value'
  });

  const isMounted = useRef(false); // Ref pour suivre le montage initial

  // Chargement des données du produit en mode édition
  useEffect(() => {
    isMounted.current = false; // Réinitialiser au cas où le composant est réutilisé pour un autre produit
    if (isEditMode && product) {
      // 1. Reconstruire productOptions à partir des données du produit existant
      const loadedProductOptions = (product.options || []).map((optDefinition, index) => {
          const optionId = `o${index + 1}`;
          optionIdCounter = Math.max(optionIdCounter, index + 1);
          
          // Trouver toutes les valeurs uniques pour ce type d'option parmi les variations existantes
          const uniqueValueNames = [...new Set(
              (product.variations || [])
                  .map(v => v.options && v.options[optDefinition.name]) // Extraire la valeur pour l'option actuelle
                  .filter(Boolean) // Filtrer les undefined/null
          )];

          // Créer les objets valeur avec des ID uniques
          const values = uniqueValueNames.map((valueName, valueIndex) => {
              const valueId = `v${valueIndex + 1 + (index + 1) * 1000}`; // Assurer l'unicité de l'ID de valeur
              valueIdCounter = Math.max(valueIdCounter, valueIndex + 1 + (index + 1) * 1000);
              return { id: valueId, name: valueName };
          });

          return { 
              id: optionId,
              name: optDefinition.name,
              values: values
          };
      });
      setProductOptions(loadedProductOptions);

      // 2. Mettre à jour le reste du formData
      setFormData(prev => ({ 
          ...prev,
          ...product,
          // Adapter les images existantes vers la nouvelle structure
          images: [
            // Image principale du produit (si elle existe)
            ...(product.mainImage ? [{
              id: 'main_existing',
              existingUrl: product.mainImage,
              alt_text: `Image principale de ${product.name}`,
              position: 1,
              is_main: true,
              product_variant_id: null
            }] : []),
            // Images des variants (si elles existent)
            ...((product.variations || []).map((v, index) => 
              v.image_url ? {
                id: `variant_${v.id || index}_existing`,
                existingUrl: v.image_url,
                alt_text: `Image de ${v.title}`,
                position: index + 2, // Après l'image principale
                is_main: false,
                product_variant_id: v.id
              } : null
            ).filter(Boolean))
          ],
          price: product.price ? product.price.toString() : '',
          discountPrice: product.discountPrice ? product.discountPrice.toString() : '',
          stockLevel: product.stockLevel ? product.stockLevel.toString() : '',
          barcode: product.barcode || '',
          has_variants: product.has_variants || false,
          options: product.options || [], // Conserver la structure originale de la DB pour référence si besoin
          variations: (product.variations || []).map((v, index) => ({
              id: v.id || `temp_${index}`,
              options: v.options || {},
              title: v.title || Object.values(v.options || {}).join(' / '),
              price: v.price ? v.price.toString() : '',
              sku: v.sku || '', // Le SKU sera regénéré si la logique auto est active
              stock: v.inventory_quantity ? v.inventory_quantity.toString() : '0',
              compare_at_price: v.compare_at_price ? v.compare_at_price.toString() : '',
              barcode: v.barcode || ''
          }))
      }));
      
      // Marquer la fin du chargement initial pour CE rendu
      // requestAnimationFrame assure que le marquage se fait après le rendu initial du chargement
      requestAnimationFrame(() => { isMounted.current = true; }); 
    } else {
      // Si création, marquer comme monté immédiatement
       isMounted.current = true;
    }
    // Remarque: generateVariations ne doit PAS être une dépendance ici
  }, [product, isEditMode]); 

  const categories = [
    'Planches',
    'Voiles',
    'Gréements',
    'Accessoires',
    'Vêtements',
    'Foils'
  ];

  const subCategories = {
    'Planches': ['Débutant', 'Freeride', 'Freestyle', 'Slalom', 'Vague'],
    'Voiles': ['Freeride', 'Freestyle', 'Race', 'Vague'],
    'Gréements': ['Mâts', 'Wishbones', 'Pieds de mât', 'Extensions'],
    'Accessoires': ['Harnais', 'Protection', 'Transport', 'Entretien'],
    'Vêtements': ['Combinaisons', 'Lycras', 'Chaussons', 'Gants'],
    'Foils': ['Débutant', 'Freeride', 'Race']
  };

  // --- Gestion des Options --- 

  const addOptionType = () => {
    optionIdCounter++;
    setProductOptions(prev => [
      ...prev,
      { id: `o${optionIdCounter}`, name: '', values: [] }
    ]);
  };

  const removeOptionType = (optionId) => {
    setProductOptions(prev => prev.filter(opt => opt.id !== optionId));
    // TODO: Mettre à jour les variations générées plus tard
  };

  const handleOptionNameChange = (optionId, newName) => {
    setProductOptions(prev => 
      prev.map(opt => opt.id === optionId ? { ...opt, name: newName } : opt)
    );
  };

  const addOptionValue = (optionId, newValueName) => {
    if (!newValueName.trim()) return; // Ne pas ajouter de valeur vide
    
    valueIdCounter++;
    setProductOptions(prev =>
      prev.map(opt => {
        if (opt.id === optionId) {
          // Vérifier si la valeur existe déjà (insensible à la casse)
          const exists = opt.values.some(val => val.name.toLowerCase() === newValueName.trim().toLowerCase());
          if (!exists) {
            return { 
              ...opt, 
              values: [...opt.values, { id: `v${valueIdCounter}`, name: newValueName.trim() }] 
            };
          }
        }
        return opt;
      })
    );
    // TODO: Mettre à jour les variations générées plus tard
  };

  const removeOptionValue = (optionId, valueId) => {
    setProductOptions(prev => 
      prev.map(opt => 
        opt.id === optionId ? { ...opt, values: opt.values.filter(val => val.id !== valueId) } : opt
      )
    );
    // TODO: Mettre à jour les variations générées plus tard
  };

  // Composant interne pour gérer l'ajout de valeurs d'option
  const OptionValueInput = ({ optionId }) => {
    const [valueName, setValueName] = useState('');

    const handleAdd = (e) => {
      if (e.key === 'Enter' || e.type === 'click') {
        e.preventDefault();
        addOptionValue(optionId, valueName);
        setValueName(''); // Réinitialiser le champ
      }
    };

    return (
      <div className="flex items-center space-x-2 mt-1">
        <input
          type="text"
          value={valueName}
          onChange={(e) => setValueName(e.target.value)}
          onKeyDown={handleAdd} // Ajouter sur Entrée
          placeholder="Nouvelle valeur (ex: Rouge)"
          className="flex-grow shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
        />
        <button
          type="button"
          onClick={handleAdd} // Ajouter sur Clic
          className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <PlusIcon className="h-4 w-4" />
        </button>
      </div>
    );
  };

  // --- Fin Gestion des Options ---

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  // --- Gestion Images Multiples ---
  let imageIdCounter = 0;

  const handleFileSelect = (file, callback) => {
      if (!file) return;
      const reader = new FileReader();
      reader.onloadend = () => {
          // Appeler le callback avec le fichier et l'URL de prévisualisation
          callback({ file: file, previewUrl: reader.result });
      };
      reader.onerror = () => {
          setSubmitError("Erreur lors de la lecture du fichier image.");
          console.error("FileReader error");
      };
      reader.readAsDataURL(file);
  };

  // Ajouter une nouvelle image générale au produit
  const addProductImage = (file, altText = '') => {
    handleFileSelect(file, (imageData) => {
      imageIdCounter++;
      const newImage = {
        id: `new_${imageIdCounter}`,
        file: imageData.file,
        previewUrl: imageData.previewUrl,
        alt_text: altText || `Image du produit`,
        position: formData.images.length + 1,
        is_main: formData.images.filter(img => img.product_variant_id === null).length === 0, // Première image = principale
        product_variant_id: null
      };
      
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, newImage]
      }));
      
      if (submitError) setSubmitError(null);
    });
  };

  // Ajouter plusieurs images en même temps
  const addMultipleProductImages = (files) => {
    const currentProductImagesCount = formData.images.filter(img => img.product_variant_id === null).length;
    
    Array.from(files).forEach((file, index) => {
      handleFileSelect(file, (imageData) => {
        imageIdCounter++;
        const newImage = {
          id: `new_${imageIdCounter}`,
          file: imageData.file,
          previewUrl: imageData.previewUrl,
          alt_text: `Image du produit ${currentProductImagesCount + index + 1}`,
          position: formData.images.length + index + 1,
          is_main: currentProductImagesCount === 0 && index === 0, // Première image = principale si aucune existe
          product_variant_id: null
        };
        
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, newImage]
        }));
      });
    });
    
    if (submitError) setSubmitError(null);
  };

  // Ajouter une image spécifique à un variant
  const addVariantImage = (file, variantId, altText = '') => {
    handleFileSelect(file, (imageData) => {
      imageIdCounter++;
      const variant = formData.variations.find(v => v.id === variantId);
      const newImage = {
        id: `new_variant_${imageIdCounter}`,
        file: imageData.file,
        previewUrl: imageData.previewUrl,
        alt_text: altText || `Image de ${variant?.title || 'variant'}`,
        position: formData.images.length + 1,
        is_main: false,
        product_variant_id: variantId
      };
      
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, newImage]
      }));
      
      if (submitError) setSubmitError(null);
    });
  };

  // Supprimer une image
  const removeImage = (imageId) => {
    setFormData(prev => {
      const updatedImages = prev.images.filter(img => img.id !== imageId);
      // Réorganiser les positions
      const reorderedImages = updatedImages.map((img, index) => ({
        ...img,
        position: index + 1
      }));
      
      // Si on supprime l'image principale, promouvoir la première image générale
      const hasMainImage = reorderedImages.some(img => img.is_main && img.product_variant_id === null);
      if (!hasMainImage) {
        const firstGeneralImage = reorderedImages.find(img => img.product_variant_id === null);
        if (firstGeneralImage) {
          firstGeneralImage.is_main = true;
        }
      }
      
      return { ...prev, images: reorderedImages };
    });
  };

  // Définir une image comme principale
  const setMainImage = (imageId) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.map(img => ({
        ...img,
        is_main: img.id === imageId && img.product_variant_id === null
      }))
    }));
  };

  // Réorganiser les images (drag & drop ou boutons)
  const reorderImages = (fromIndex, toIndex) => {
    setFormData(prev => {
      const images = [...prev.images];
      const [movedImage] = images.splice(fromIndex, 1);
      images.splice(toIndex, 0, movedImage);
      
      // Mettre à jour les positions
      const reorderedImages = images.map((img, index) => ({
        ...img,
        position: index + 1
      }));
      
      return { ...prev, images: reorderedImages };
    });
  };

  // Obtenir l'image principale du produit
  const getMainImage = () => {
    return formData.images.find(img => img.is_main && (!img.option_values || Object.keys(img.option_values).length === 0));
  };

  // Obtenir les images générales du produit (non associées à des options spécifiques)
  const getProductImages = () => {
    return formData.images.filter(img => !img.option_values || Object.keys(img.option_values).length === 0).sort((a, b) => a.position - b.position);
  };

  // Obtenir les images d'un variant spécifique (basé sur ses options)
  const getVariantImages = (variantId) => {
    const variant = formData.variations.find(v => v.id === variantId);
    if (!variant || !variant.options) return [];
    
    return formData.images.filter(img => {
      if (!img.option_values) return false;
      
      // Vérifier si l'image correspond à au moins une valeur d'option du variant
      return Object.entries(img.option_values).some(([optionName, optionValue]) => {
        return variant.options[optionName] === optionValue;
      });
    }).sort((a, b) => a.position - b.position);
  };

  // Obtenir les images pour une valeur d'option spécifique
  const getImagesForOptionValue = (optionName, optionValue) => {
    return formData.images.filter(img => 
      img.option_values && img.option_values[optionName] === optionValue
    ).sort((a, b) => a.position - b.position);
  };

  // Obtenir tous les variants qui correspondent à une valeur d'option
  const getVariantsForOptionValue = (optionName, optionValue) => {
    return formData.variations.filter(variant => 
      variant.options && variant.options[optionName] === optionValue
    );
  };

  // Ouvrir le modal de sélection d'images pour un variant
  const openImageSelectionModal = (variantId, variantTitle) => {
    const variant = formData.variations.find(v => v.id === variantId);
    setImageSelectionModal({
      isOpen: true,
      variant,
      mode: 'variant'
    });
  };

  // Fermer le modal de sélection
  const closeImageSelectionModal = () => {
    setImageSelectionModal({
      isOpen: false,
      variant: null,
      mode: 'variant'
    });
  };

  // Associer une image à une valeur d'option spécifique
  const associateImageToOptionValue = (imageId, optionName, optionValue) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.map(img => {
        if (img.id === imageId) {
          const newOptionValues = { ...img.option_values } || {};
          newOptionValues[optionName] = optionValue;
          return { ...img, option_values: newOptionValues };
        }
        return img;
      })
    }));
  };

  // Dissocier une image d'une valeur d'option
  const dissociateImageFromOptionValue = (imageId, optionName) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.map(img => {
        if (img.id === imageId && img.option_values) {
          const newOptionValues = { ...img.option_values };
          delete newOptionValues[optionName];
          return { 
            ...img, 
            option_values: Object.keys(newOptionValues).length > 0 ? newOptionValues : null 
          };
        }
        return img;
      })
    }));
  };

  // Vérifier si une image est associée à une valeur d'option spécifique du variant
  const isImageAssociatedToVariant = (imageId, variant) => {
    const image = formData.images.find(img => img.id === imageId);
    if (!image?.option_values || !variant?.options) return false;
    
    // Vérifier si l'image correspond à au moins une valeur d'option du variant
    return Object.entries(image.option_values).some(([optionName, optionValue]) => {
      return variant.options[optionName] === optionValue;
    });
  };
  // --- Fin Gestion Images Multiples ---

  // --- Génération et Gestion des Variations --- 
  const generateVariations = useCallback(() => {
    const baseSku = formData.sku || ''; // Utiliser le SKU principal comme base

    if (!formData.has_variants || productOptions.length === 0 || productOptions.some(opt => !opt.name.trim() || opt.values.length === 0)) {
      setFormData(prev => {
          if (prev.variations.length > 0) return { ...prev, variations: [] };
          return prev;
      });
      return;
    }

    const optionValuesArrays = productOptions.map(opt => opt.values);
    const combinations = cartesian(...optionValuesArrays);

    setFormData(prevFormData => { 
        const currentVariations = prevFormData.variations; 
        const currentBaseSku = prevFormData.sku || ''; // Lire aussi le SKU de base précédent

        const newVariations = combinations.map((combo, index) => {
            const optionsObject = {};
            const titleParts = [];
            const valueIds = []; 
            const skuParts = [currentBaseSku]; // Commencer le SKU avec le SKU de base
            
            productOptions.forEach((opt, i) => {
                optionsObject[opt.name] = combo[i].name;
                titleParts.push(combo[i].name);
                valueIds.push(combo[i].id); 
                // Générer la partie du SKU pour cette valeur d'option
                const valueName = combo[i].name;
                const skuPart = valueName.substring(0, 2).toUpperCase();
                skuParts.push(skuPart);
            });

            const generatedSku = skuParts.filter(Boolean).join('-'); // Joindre les parties avec un tiret

            const existingVariation = currentVariations.find(v =>
                JSON.stringify(v.options) === JSON.stringify(optionsObject)
            );

            const tempId = `temp_${valueIds.sort().join('_')}`;

            return {
                id: existingVariation?.id || tempId, 
                options: optionsObject,
                title: titleParts.join(' / '),
                price: existingVariation?.price || '',
                // Utiliser le SKU généré
                sku: generatedSku, 
                stock: existingVariation?.stock || '0',
                compare_at_price: existingVariation?.compare_at_price || '',
                barcode: existingVariation?.barcode || '',
                image: existingVariation?.image || null
            };
        });

        if (JSON.stringify(currentVariations) !== JSON.stringify(newVariations)) {
            return { ...prevFormData, variations: newVariations };
        }
        return prevFormData; 
    });

  // Dépendre aussi de formData.sku pour regénérer si le SKU de base change
  }, [productOptions, formData.has_variants, formData.sku]); 

  // useEffect POUR LA REGENERATION des variations (Modifié)
  useEffect(() => {
      // Ne pas exécuter la logique de régénération pendant/avant la fin du chargement initial
      if (!isMounted.current) {
          console.log("useEffect [options/variants/sku]: Skipping initial mount/load phase");
          return; 
      }
      
      console.log("useEffect [options/variants/sku]: Running generation/clearing logic...");
      if (formData.has_variants) {
          generateVariations();
      } else {
          // Si on désactive les variations, s'assurer que le tableau est vide
          if (formData.variations.length > 0) {
              console.log("Clearing variations because has_variants is false.");
              setFormData(prev => ({ ...prev, variations: [] }));
          }
      }
  // Dépendances qui déclenchent la RE-génération
  // generateVariations est inclus car sa définition dépend de certaines de ces dépendances via useCallback
  }, [productOptions, formData.has_variants, formData.sku, generateVariations]); 
  
  const handleVariationDetailChange = (variationIndex, field, value) => {
    setFormData(prev => {
      const updatedVariations = [...prev.variations];
      updatedVariations[variationIndex] = {
        ...updatedVariations[variationIndex],
        [field]: value
      };
      return { ...prev, variations: updatedVariations };
    });
    if (errors[`variation_${variationIndex}_${field}`]) {
      setErrors(prev => ({ ...prev, [`variation_${variationIndex}_${field}`]: null }));
    }
  };
  // --- Fin Gestion des Variations ---

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Le nom du produit est requis';
    if (!formData.sku.trim() && !formData.has_variants) newErrors.sku = 'La référence est requise (produit simple)'; // SKU base requis si pas de variations
    if (!formData.category) newErrors.category = 'La catégorie est requise';

    if (formData.has_variants) {
      if (!formData.sku.trim()) newErrors.sku = 'La référence de base est requise pour générer les SKU des variations'; // SKU base requis pour variations
      // Validation des options
      if (productOptions.length === 0) {
        newErrors.options = 'Au moins une option est requise pour un produit avec variations.';
      } else {
        productOptions.forEach(opt => {
          if (!opt.name.trim()) newErrors[`optionName_${opt.id}`] = 'Le nom de l\'option ne peut pas être vide.';
          if (opt.values.length === 0) newErrors[`optionValues_${opt.id}`] = 'Au moins une valeur est requise pour cette option.';
        });
      }
      // Validation des variations générées
      if (formData.variations.length === 0 && productOptions.length > 0 && productOptions.every(opt => opt.values.length > 0)) {
         newErrors.variations = 'Aucune variation générée. Vérifiez vos options et le SKU de base.';
      } else {
          formData.variations.forEach((variation, index) => {
            if (!variation.price || isNaN(Number(variation.price)) || Number(variation.price) <= 0) {
              newErrors[`variation_${index}_price`] = 'Prix requis';
            }
            if (!variation.stock || isNaN(Number(variation.stock)) || Number(variation.stock) < 0) {
              newErrors[`variation_${index}_stock`] = 'Stock requis';
            }
          });
      }
    } else {
      // Validation pour produit simple
      if (!formData.price.trim() || isNaN(Number(formData.price)) || Number(formData.price) <= 0) newErrors.price = 'Prix valide requis';
      if (!formData.stockLevel.trim() || isNaN(Number(formData.stockLevel)) || Number(formData.stockLevel) < 0) newErrors.stockLevel = 'Stock valide requis';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // --- handleSubmit (avec upload intégré ET appel API pour create/update) --- 
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm() || isSubmitting) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // 1. Uploader toutes les nouvelles images
      const uploadedImages = [];
      
      for (const image of formData.images) {
        if (image.file) {
          // Nouvelle image à uploader
          const uploadedUrl = await uploadImage(image.file);
          if (!uploadedUrl) throw new Error(`Échec de l'upload de l'image ${image.alt_text}.`);
          
          uploadedImages.push({
            ...image,
            image_url: uploadedUrl,
            file: undefined, // Nettoyer le file après upload
            previewUrl: undefined // Nettoyer la preview après upload
          });
        } else if (image.existingUrl) {
          // Image existante
          uploadedImages.push({
            ...image,
            image_url: image.existingUrl
          });
        }
      }

      // 2. Préparer les données finales pour l'API
      const finalProductOptions = formData.has_variants ? productOptions.map(opt => ({ name: opt.name })) : null;
      
      const finalVariations = formData.has_variants ? formData.variations.map((v, index) => ({
        id: typeof v.id === 'string' && v.id.startsWith('temp_') ? undefined : v.id, 
        title: v.title,
        options: v.options,
        price: Number(v.price),
        inventory_quantity: Number(v.stock),
        sku: v.sku,
        compare_at_price: v.compare_at_price ? Number(v.compare_at_price) : null,
        barcode: v.barcode || null
      })) : [];
      
      // Récupérer l'image principale pour rétrocompatibilité
      const mainImage = uploadedImages.find(img => img.is_main && img.product_variant_id === null);
      
      const formattedData = {
        name: formData.name,
        sku: formData.sku,
        category: formData.category,
        subCategory: formData.subCategory,
        status: formData.status,
        description: formData.description,
        barcode: formData.has_variants ? null : formData.barcode || null,
        has_variants: formData.has_variants,
        mainImage: mainImage?.image_url || null, // Image principale pour rétrocompatibilité
        price: formData.has_variants ? null : Number(formData.price),
        discountPrice: formData.has_variants ? null : (formData.discountPrice ? Number(formData.discountPrice) : null),
        stockLevel: formData.has_variants ? null : Number(formData.stockLevel),
        options: finalProductOptions,
        variations: finalVariations,
        // Nouvelle structure d'images multiples
        images: uploadedImages.map(img => ({
          image_url: img.image_url,
          alt_text: img.alt_text,
          position: img.position,
          is_main: img.is_main,
          option_values: img.option_values || null
        }))
      };
      
      console.log(`Données formatées pour ${isEditMode ? 'Update' : 'Create'}:`, formattedData);
      
      // 3. Appeler l'API appropriée (Create ou Update via onSave)
      if (isEditMode) {
          // En mode édition, on appelle la fonction passée via onSave (qui est handleUpdateProduct)
          await onSave(formattedData); 
      } else {
          // En mode création, on appelle directement createProduct
          const result = await createProduct(formattedData);
          console.log("Réponse API Create:", result);
      }
      
      // Si tout va bien (pas d'erreur levée), fermer le formulaire
      onClose(); 

    } catch (error) {
        console.error(`Erreur lors de ${isEditMode ? 'la mise à jour' : 'la création'}:`, error);
        setSubmitError(error.message || `Une erreur est survenue lors de ${isEditMode ? 'la mise à jour' : 'la création'}.`);
    } finally {
        setIsSubmitting(false);
    }
  };
  // --- Fin handleSubmit ---

  // --- Fonction Upload (inchangée mais appelée par handleSubmit) --- 
  const uploadImage = async (file) => {
      if (!file) return null;
      let imageUrl = null;
      try {
          const fileExt = file.name.split('.').pop();
          const fileName = `${Date.now()}_${Math.random().toString(36).substring(2)}.${fileExt}`;
          const filePath = `public/${fileName}`;
          let { error: uploadError } = await supabase.storage.from('product-images').upload(filePath, file);
          if (uploadError) throw uploadError;
          const { data } = supabase.storage.from('product-images').getPublicUrl(filePath);
          if (!data || !data.publicUrl) throw new Error("URL publique non trouvée.");
          imageUrl = data.publicUrl;
      } catch (error) {
          console.error("Erreur Upload:", error);
          // L'erreur sera rattrapée par handleSubmit
          throw error; 
      }
      return imageUrl;
  };
  // --- Fin Fonction Upload ---

  // Adapter variationRows pour utiliser la nouvelle structure d'images multiples
  const variationRows = useMemo(() => {
     const placeholderImageUrl = 'https://placehold.co/40x40/png?text=?'; 
     return formData.variations.map((variation, index) => {
       // Récupérer les images du variant ou l'image principale du produit
       const variantImages = getVariantImages(variation.id);
       const productImages = getProductImages();
       const mainImage = getMainImage();
       
       // Déterminer l'URL à afficher (première image du variant OU image principale OU placeholder)
       const displayImageUrl = variantImages[0]?.previewUrl || variantImages[0]?.existingUrl || variantImages[0]?.image_url || 
                              mainImage?.previewUrl || mainImage?.existingUrl || mainImage?.image_url || 
                              placeholderImageUrl;
       
       return (
         <tr key={variation.id}>
                                      <td className="px-3 py-2">
              <div className="flex items-center space-x-2">
                  <div className="flex -space-x-1">
                    {variantImages.slice(0, 3).map((img, imgIndex) => (
                      <div key={img.id} className="relative group">
                        <img 
                          src={img.previewUrl || img.existingUrl || img.image_url}
                          alt={img.alt_text}
                          className="h-8 w-8 rounded border-2 border-white object-cover shadow-sm"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(img.id)}
                          className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Supprimer"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    {variantImages.length > 3 && (
                      <div className="h-8 w-8 rounded border-2 border-white bg-gray-100 flex items-center justify-center text-xs text-gray-500">
                        +{variantImages.length - 3}
                      </div>
                    )}
                    {variantImages.length === 0 && (
                      <img 
                        src={displayImageUrl}
                        alt={`Variation ${variation.title}`}
                        className={`h-8 w-8 rounded object-cover ${displayImageUrl === placeholderImageUrl ? 'bg-gray-100' : ''}`}
                      />
                    )}
                  </div>
                  <button
                      type="button"
                      onClick={() => openImageSelectionModal(variation.id, variation.title)}
                      className="cursor-pointer text-primary-600 hover:text-primary-800 flex-shrink-0 p-1 rounded hover:bg-primary-50"
                      title="Associer des images par valeur d'option"
                      disabled={isSubmitting}
                  >
                      <PlusIcon className="h-4 w-4" />
                  </button>
              </div>
           </td>
           <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">{variation.title}</td>
           <td className="px-3 py-2">
             <input
               type="text"
               value={variation.price}
               onChange={(e) => handleVariationDetailChange(index, 'price', e.target.value)}
               placeholder="0"
               className={`w-24 shadow-sm focus:ring-primary-500 focus:border-primary-500 block sm:text-sm border-gray-300 rounded-md ${errors[`variation_${index}_price`] ? 'border-red-300' : ''}`}
             />
             {errors[`variation_${index}_price`] && <p className="mt-1 text-xs text-red-600">{errors[`variation_${index}_price`]}</p>}
           </td>
           <td className="px-3 py-2">
             <input
               type="text"
               value={variation.compare_at_price}
               onChange={(e) => handleVariationDetailChange(index, 'compare_at_price', e.target.value)}
               placeholder="(Optionnel)"
               className="w-24 shadow-sm focus:ring-primary-500 focus:border-primary-500 block sm:text-sm border-gray-300 rounded-md"
             />
           </td>
           <td className="px-3 py-2">
             <input
               type="text"
               value={variation.sku}
               readOnly
               onChange={(e) => handleVariationDetailChange(index, 'sku', e.target.value)}
               placeholder="Généré auto."
               title={variation.sku}
               className={`w-32 shadow-sm focus:ring-primary-500 focus:border-primary-500 block sm:text-sm border-gray-300 rounded-md bg-gray-100 text-gray-500`}
             />
           </td>
           <td className="px-3 py-2">
             <input
               type="text"
               value={variation.barcode}
               onChange={(e) => handleVariationDetailChange(index, 'barcode', e.target.value)}
               placeholder="(Optionnel)"
               className="w-32 shadow-sm focus:ring-primary-500 focus:border-primary-500 block sm:text-sm border-gray-300 rounded-md"
             />
           </td>
           <td className="px-3 py-2">
             <input
               type="text"
               value={variation.stock}
               onChange={(e) => handleVariationDetailChange(index, 'stock', e.target.value)}
               placeholder="0"
               className={`w-20 shadow-sm focus:ring-primary-500 focus:border-primary-500 block sm:text-sm border-gray-300 rounded-md ${errors[`variation_${index}_stock`] ? 'border-red-300' : ''}`}
             />
             {errors[`variation_${index}_stock`] && <p className="mt-1 text-xs text-red-600">{errors[`variation_${index}_stock`]}</p>}
           </td>
         </tr>
       );
     });
  }, [formData.variations, formData.mainImage, errors, handleVariationDetailChange, formData.has_variants, isSubmitting]);

  return (
    <div className="bg-white rounded-lg shadow-xl w-full max-h-[90vh] overflow-hidden flex flex-col relative">
        
        {/* Loading Overlay responsive */}
        {isSubmitting && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
            <div className="flex flex-col items-center p-4">
                <div className={`animate-spin rounded-full border-t-2 border-b-2 border-primary-500 mb-4 ${window.innerWidth < 768 ? 'h-8 w-8' : 'h-12 w-12'}`}></div>
                <p className={`${window.innerWidth < 768 ? 'text-base' : 'text-lg'} text-gray-600 text-center`}>Sauvegarde en cours...</p>
            </div>
          </div>
        )}

        {/* Header responsive */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className={`${window.innerWidth < 768 ? 'text-lg' : 'text-xl'} font-medium text-gray-900`}>
            {isEditMode ? 'Modifier produit' : 'Nouveau produit'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 flex-shrink-0"
            disabled={isSubmitting}
          >
            <XMarkIcon className="h-5 sm:h-6 w-5 sm:w-6" />
          </button>
        </div>
        
        {submitError && (
            <div className="p-3 sm:p-4 bg-red-100 text-red-700 text-xs sm:text-sm">
                Erreur: {submitError}
            </div>
        )}
        <div className={`flex-1 overflow-y-auto p-4 sm:p-6 ${isSubmitting ? 'opacity-50' : ''}`}>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 gap-y-4 sm:gap-y-6 gap-x-4 sm:grid-cols-6">
              {/* Informations générales */}
              <div className="sm:col-span-6">
                <h3 className={`${window.innerWidth < 768 ? 'text-base' : 'text-lg'} font-medium text-gray-900 mb-3 sm:mb-4`}>
                  Informations générales
                </h3>
              </div>
              
              {/* Nom du produit */}
              <div className="col-span-1 sm:col-span-4">
                <label htmlFor="name" className="block text-xs sm:text-sm font-medium text-gray-700">
                  Nom du produit *
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    name="name"
                    id="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full text-xs sm:text-sm border-gray-300 rounded-md ${errors.name ? 'border-red-300' : ''}`}
                  />
                  {errors.name && <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.name}</p>}
                </div>
              </div>

              {/* SKU */}
              <div className="col-span-1 sm:col-span-2">
                <label htmlFor="sku" className="block text-xs sm:text-sm font-medium text-gray-700">
                  <span className="hidden sm:inline">Référence (SKU)</span>
                  <span className="sm:hidden">SKU</span>
                  {formData.has_variants ? ' base' : ''} *
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    name="sku"
                    id="sku"
                    value={formData.sku}
                    onChange={handleChange}
                    className={`shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full text-xs sm:text-sm border-gray-300 rounded-md ${errors.sku ? 'border-red-300' : ''}`}
                  />
                  {errors.sku && <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.sku}</p>}
                </div>
              </div>

              {/* Catégorie */}
              <div className="col-span-1 sm:col-span-3">
                <label htmlFor="category" className="block text-xs sm:text-sm font-medium text-gray-700">
                  Catégorie *
                </label>
                <div className="mt-1">
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className={`shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full text-xs sm:text-sm border-gray-300 rounded-md ${errors.category ? 'border-red-300' : ''}`}
                  >
                    <option value="">Sélectionnez une catégorie</option>
                    {categories.map(category => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                  {errors.category && <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.category}</p>}
                </div>
              </div>

              {/* Sous-catégorie */}
              <div className="col-span-1 sm:col-span-3">
                <label htmlFor="subCategory" className="block text-xs sm:text-sm font-medium text-gray-700">
                  Sous-catégorie
                </label>
                <div className="mt-1">
                  <select
                    id="subCategory"
                    name="subCategory"
                    value={formData.subCategory}
                    onChange={handleChange}
                    disabled={!formData.category}
                    className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full text-xs sm:text-sm border-gray-300 rounded-md"
                  >
                    <option value="">Sélectionnez une sous-catégorie</option>
                    {formData.category && subCategories[formData.category]?.map(subCategory => (
                      <option key={subCategory} value={subCategory}>
                        {subCategory}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Description */}
              <div className="col-span-1 sm:col-span-6">
                <label htmlFor="description" className="block text-xs sm:text-sm font-medium text-gray-700">
                  Description
                </label>
                <div className="mt-1">
                  <textarea
                    id="description"
                    name="description"
                    rows={window.innerWidth < 768 ? 2 : 3}
                    value={formData.description}
                    onChange={handleChange}
                    className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full text-xs sm:text-sm border-gray-300 rounded-md"
                  />
                </div>
              </div>

              {/* Images du Produit */}
              <div className="sm:col-span-6">
                <div className="flex justify-between items-center mb-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Images du produit
                  </label>
                  <label 
                    htmlFor="add-product-image"
                    className="cursor-pointer bg-primary-600 py-2 px-3 border border-transparent rounded-md shadow-sm text-sm leading-4 font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    <PlusIcon className="h-4 w-4 inline-block mr-1" />
                    Ajouter des images
                    <input 
                      id="add-product-image"
                      type="file"
                      accept="image/*"
                      multiple
                      className="sr-only"
                      onChange={(e) => {
                        if (e.target.files && e.target.files.length > 0) {
                          addMultipleProductImages(e.target.files);
                        }
                        e.target.value = ''; // Reset pour permettre le même fichier
                      }}
                      disabled={isSubmitting}
                    />
                  </label>
                </div>
                
                {/* Grille d'images */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {getProductImages().map((image, index) => (
                    <div key={image.id} className="relative group">
                      <div className="aspect-square rounded-lg border border-gray-200 overflow-hidden bg-gray-50">
                        <img 
                          src={image.previewUrl || image.existingUrl || image.image_url} 
                          alt={image.alt_text}
                          className="h-full w-full object-cover"
                        />
                      </div>
                      
                      {/* Badge image principale */}
                      {image.is_main && (
                        <div className="absolute top-2 left-2 bg-primary-600 text-white text-xs px-2 py-1 rounded">
                          Principale
                        </div>
                      )}
                      
                      {/* Actions */}
                      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex space-x-1">
                        {!image.is_main && (
                          <button
                            type="button"
                            onClick={() => setMainImage(image.id)}
                            className="bg-gray-800 bg-opacity-75 text-white p-1 rounded hover:bg-opacity-100"
                            title="Définir comme image principale"
                          >
                            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18l3.09-6.26L18 10l-4.91-1.74L10 2 6.91 8.26 2 10l4.91 1.74L10 18z" clipRule="evenodd" />
                            </svg>
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => removeImage(image.id)}
                          className="bg-red-600 bg-opacity-75 text-white p-1 rounded hover:bg-opacity-100"
                          title="Supprimer l'image"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                      
                      {/* Position */}
                      <div className="absolute bottom-2 left-2 bg-gray-800 bg-opacity-75 text-white text-xs px-2 py-1 rounded">
                        {image.position}
                      </div>
                    </div>
                  ))}
                  
                  {/* Placeholder si aucune image */}
                  {getProductImages().length === 0 && (
                    <div className="aspect-square rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400">
                      <div className="text-center">
                        <PhotoIcon className="h-8 w-8 mx-auto mb-2" />
                        <p className="text-xs">Aucune image</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Toggle Variations */}
              <div className="sm:col-span-6">
                <div className="relative flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="has_variants"
                      name="has_variants"
                      type="checkbox"
                      checked={formData.has_variants}
                      onChange={handleChange}
                      className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300 rounded"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="has_variants" className="font-medium text-gray-700">
                      Ce produit a plusieurs options (taille, couleur, etc.)
                    </label>
                  </div>
                </div>
              </div>

              {/* Section Prix/Stock/Barcode (conditionnelle pour produit simple) */}
              {!formData.has_variants && (
                <>
                  <div className="sm:col-span-6">
                     <h3 className="text-lg font-medium text-gray-900 my-4 border-t pt-4">Prix, Stock & Code-barres (Produit Simple)</h3>
                  </div>
                  {/* Prix */}
                  <div className="sm:col-span-2">
                    <label htmlFor="price" className="block text-sm font-medium text-gray-700">
                      Prix (€) *
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <input
                        type="text"
                        name="price"
                        id="price"
                        value={formData.price}
                        onChange={handleChange}
                        className={`focus:ring-primary-500 focus:border-primary-500 block w-full pr-12 sm:text-sm border-gray-300 rounded-md ${errors.price ? 'border-red-300' : ''}`}
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 sm:text-sm">€</span>
                      </div>
                    </div>
                    {errors.price && <p className="mt-1 text-sm text-red-600">{errors.price}</p>}
                  </div>
                  {/* Prix promotionnel */}
                  <div className="sm:col-span-2">
                    <label htmlFor="discountPrice" className="block text-sm font-medium text-gray-700">
                      Prix promotionnel (€)
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <input
                        type="text"
                        name="discountPrice"
                        id="discountPrice"
                        value={formData.discountPrice}
                        onChange={handleChange}
                        className={`focus:ring-primary-500 focus:border-primary-500 block w-full pr-12 sm:text-sm border-gray-300 rounded-md ${errors.discountPrice ? 'border-red-300' : ''}`}
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 sm:text-sm">€</span>
                      </div>
                    </div>
                    {errors.discountPrice && <p className="mt-1 text-sm text-red-600">{errors.discountPrice}</p>}
                  </div>
                  {/* Stock */}
                  <div className="sm:col-span-2">
                    <label htmlFor="stockLevel" className="block text-sm font-medium text-gray-700">
                      Stock disponible *
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        name="stockLevel"
                        id="stockLevel"
                        value={formData.stockLevel}
                        onChange={handleChange}
                        className={`shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.stockLevel ? 'border-red-300' : ''}`}
                      />
                      {errors.stockLevel && <p className="mt-1 text-sm text-red-600">{errors.stockLevel}</p>}
                    </div>
                  </div>
                  {/* Code-barres (Produit Simple) */}
                  <div className="sm:col-span-4">
                     <label htmlFor="barcode" className="block text-sm font-medium text-gray-700">
                        Code-barres (EAN, UPC, etc.)
                     </label>
                     <div className="mt-1">
                        <input
                           type="text"
                           name="barcode"
                           id="barcode"
                           value={formData.barcode}
                           onChange={handleChange}
                           className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        />
                        {/* Ajouter gestion d'erreur si besoin: errors.barcode */}
                     </div>
                  </div>
                </>
              )}

              {/* Statut */}
              <div className="sm:col-span-6">
                 <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                    Statut
                  </label>
                 <div className="mt-1">
                    <select
                      id="status"
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    >
                     <option value="active">Actif</option>
                     <option value="inactive">Inactif</option>
                     <option value="draft">Brouillon</option>
                   </select>
                 </div>
              </div>

              {/* Section Options (si variations activées) */}
              {formData.has_variants && (
                <div className="sm:col-span-6 border-t pt-6 mt-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Options du produit</h3>
                  {errors.options && <p className="mb-4 text-sm text-red-600">{errors.options}</p>}
                  
                  <div className="space-y-4">
                    {productOptions.map((option, index) => (
                      <div key={option.id} className="border border-gray-200 rounded-md p-4">
                        <div className="flex items-center justify-between mb-2">
                          <label htmlFor={`optionName_${option.id}`} className="block text-sm font-medium text-gray-700">
                            Option {index + 1}
                          </label>
                          <button
                            type="button"
                            onClick={() => removeOptionType(option.id)}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                        <input
                          type="text"
                          id={`optionName_${option.id}`}
                          value={option.name}
                          onChange={(e) => handleOptionNameChange(option.id, e.target.value)}
                          placeholder="Nom de l'option (ex: Couleur)"
                          className={`shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md mb-2 ${errors[`optionName_${option.id}`] ? 'border-red-300' : ''}`}
                        />
                         {errors[`optionName_${option.id}`] && <p className="mt-1 text-sm text-red-600">{errors[`optionName_${option.id}`]}</p>}
                        
                        {/* Valeurs de l'option */}
                        <label className="block text-sm font-medium text-gray-700 mt-3 mb-1">
                          Valeurs de l'option
                        </label>
                        {errors[`optionValues_${option.id}`] && <p className="mb-1 text-sm text-red-600">{errors[`optionValues_${option.id}`]}</p>}
                        <div className="flex flex-wrap gap-2 mb-2">
                          {option.values.map(value => (
                            <span key={value.id} className="inline-flex items-center py-0.5 pl-2.5 pr-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800">
                              {value.name}
                              <button
                                type="button"
                                onClick={() => removeOptionValue(option.id, value.id)}
                                className="flex-shrink-0 ml-1.5 h-4 w-4 rounded-full inline-flex items-center justify-center text-primary-400 hover:bg-primary-200 hover:text-primary-500 focus:outline-none focus:bg-primary-600 focus:text-white"
                              >
                                <span className="sr-only">Remove value</span>
                                <svg className="h-2 w-2" stroke="currentColor" fill="none" viewBox="0 0 8 8">
                                  <path strokeLinecap="round" strokeWidth="1.5" d="M1 1l6 6m0-6L1 7" />
                                </svg>
                              </button>
                            </span>
                          ))}
                        </div>
                        <OptionValueInput optionId={option.id} />
                      </div>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={addOptionType}
                    disabled={productOptions.length >= 3} // Limiter à 3 options max pour l'instant
                    className={`mt-4 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${productOptions.length >= 3 ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" aria-hidden="true" />
                    Ajouter une option (max 3)
                  </button>
                </div>
              )}

              {/* Section Variations (si variations activées et options valides) */}
              {formData.has_variants && productOptions.length > 0 && productOptions.every(opt => opt.name.trim() && opt.values.length > 0) && (
                 <div className="sm:col-span-6 border-t pt-6 mt-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Variations du produit</h3>
                    {errors.variations && <p className="mb-4 text-sm text-red-600">{errors.variations}</p>}
                    
                    {formData.variations.length > 0 ? (
                       <div className="overflow-x-auto -mx-6">
                          <table className="min-w-full divide-y divide-gray-200">
                             <thead className="bg-gray-50">
                                <tr>
                                   <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Image</th>
                                   <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Variation</th>
                                   <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Prix *</th>
                                   <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Prix Barré</th>
                                   <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SKU (Auto)</th>
                                   <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code-barres</th>
                                   <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stock *</th>
                                </tr>
                             </thead>
                             <tbody className="bg-white divide-y divide-gray-200">
                               {variationRows}
                             </tbody>
                          </table>
                       </div>
                    ) : (
                       <p className="text-sm text-gray-500 italic">Aucune variation générée. Ajoutez des options et des valeurs ci-dessus.</p>
                    )}
                 </div>
              )}
            </div>
          </form>
        </div>
        
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting} 
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:bg-primary-400"
          >
            {isSubmitting ? 'Sauvegarde...' : (isEditMode ? 'Mettre à jour' : 'Ajouter le produit')}
          </button>
        </div>

        {/* Modal de sélection d'images pour variants */}
        {imageSelectionModal.isOpen && imageSelectionModal.variant && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              {/* Overlay */}
              <div 
                className="fixed inset-0 transition-opacity"
                onClick={closeImageSelectionModal}
              >
                <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
              </div>

              {/* Modal */}
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">
                        Associer des images pour "{imageSelectionModal.variant.title}"
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        Sélectionnez les valeurs d'options pour associer les images
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={closeImageSelectionModal}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>

                  {formData.images.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <PhotoIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p>Aucune image disponible</p>
                      <p className="text-sm">Ajoutez d'abord des images au produit</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Sections par valeur d'option */}
                      {productOptions.map((option) => {
                        const variantOptionValue = imageSelectionModal.variant.options[option.name];
                        
                        return (
                          <div key={option.name} className="border rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="font-medium text-gray-900">
                                {option.name}: <span className="text-primary-600">{variantOptionValue}</span>
                              </h4>
                              <span className="text-sm text-gray-500">
                                {getImagesForOptionValue(option.name, variantOptionValue).length} image(s)
                              </span>
                            </div>
                            
                            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                              {formData.images.filter(img => !img.option_values || img.option_values[option.name] !== variantOptionValue).map((image) => {
                                const isAssociated = image.option_values && image.option_values[option.name] === variantOptionValue;
                                const isGeneralImage = !image.option_values;
                                
                                return (
                                  <div 
                                    key={image.id} 
                                    className={`relative cursor-pointer border-2 rounded-lg overflow-hidden transition-all ${
                                      isAssociated 
                                        ? 'border-primary-500 bg-primary-50' 
                                        : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                    onClick={() => {
                                      if (isAssociated) {
                                        dissociateImageFromOptionValue(image.id, option.name);
                                      } else {
                                        associateImageToOptionValue(image.id, option.name, variantOptionValue);
                                      }
                                    }}
                                  >
                                    <div className="aspect-square">
                                      <img 
                                        src={image.previewUrl || image.existingUrl || image.image_url} 
                                        alt={image.alt_text}
                                        className="h-full w-full object-cover"
                                      />
                                    </div>
                                    
                                    {/* Checkbox indicator */}
                                    <div className={`absolute top-1 right-1 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                      isAssociated 
                                        ? 'bg-primary-600 border-primary-600' 
                                        : 'bg-white border-gray-300'
                                    }`}>
                                      {isAssociated && (
                                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                      )}
                                    </div>

                                    {/* Badge image principale */}
                                    {image.is_main && (
                                      <div className="absolute top-1 left-1 bg-yellow-500 text-white text-xs px-1 py-0.5 rounded text-[10px]">
                                        P
                                      </div>
                                    )}

                                    {/* Associations existantes */}
                                    {image.option_values && Object.keys(image.option_values).length > 0 && (
                                      <div className="absolute bottom-1 left-1 bg-gray-800 bg-opacity-75 text-white text-xs px-1 py-0.5 rounded text-[10px]">
                                        {Object.values(image.option_values).join(', ')}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                            
                            {/* Images déjà associées à cette valeur d'option */}
                            {getImagesForOptionValue(option.name, variantOptionValue).length > 0 && (
                              <div className="mt-3 pt-3 border-t">
                                <p className="text-sm font-medium text-gray-700 mb-2">Images associées :</p>
                                <div className="flex flex-wrap gap-2">
                                  {getImagesForOptionValue(option.name, variantOptionValue).map((image) => (
                                    <div key={image.id} className="relative group">
                                      <img 
                                        src={image.previewUrl || image.existingUrl || image.image_url} 
                                        alt={image.alt_text}
                                        className="h-12 w-12 rounded border-2 border-primary-500 object-cover"
                                      />
                                      <button
                                        type="button"
                                        onClick={() => dissociateImageFromOptionValue(image.id, option.name)}
                                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                                      >
                                        ×
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={closeImageSelectionModal}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Terminé
                  </button>
                  <p className="text-sm text-gray-500 self-center mr-4">
                    {getVariantImages(imageSelectionModal.variant.id).length} image(s) pour ce variant
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
  );
};

export default ProductForm; 